import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLenis } from "lenis/react";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { IMAGES } from "../data/siteContent";
import { RevealLine, EASE } from "./shared";

const STATS = [
    { value: "40+", label: "Years of legacy" },
    { value: "6", label: "Product lines" },
    { value: "04", label: "Core industries" },
];

const Hero = () => {
    const ref = useRef(null);
    const lenis = useLenis();
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

    return (
        <section
            id="top"
            ref={ref}
            data-testid="hero-section"
            className="relative min-h-screen flex flex-col justify-end overflow-hidden"
        >
            <motion.div className="absolute inset-0" style={{ y: bgY, scale: 1.12 }}>
                <img
                    src={IMAGES.hero}
                    alt="Precision machined steel close-up"
                    className="w-full h-full object-cover"
                />
            </motion.div>
            <div className="absolute inset-0 bg-[#FAFAFA]/85" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#FAFAFA] via-[#FAFAFA]/40 to-[#FAFAFA]/70" />

            <motion.div style={{ opacity: fade }} className="relative z-10 px-6 md:px-12 pb-10 md:pb-14 pt-40">
                <RevealLine delay={0.15}>
                    <span className="inline-flex items-center gap-3 text-xs md:text-sm uppercase tracking-[0.3em] text-copper">
                        <span className="w-8 h-px bg-copper inline-block" />
                        First Choice Trading Company — Ghaziabad, India
                    </span>
                </RevealLine>

                <h1 className="font-heading uppercase font-semibold leading-[0.95] tracking-tight mt-6 text-[clamp(3rem,10.5vw,9.5rem)]">
                    <RevealLine delay={0.3}>Precision</RevealLine>
                    <RevealLine delay={0.42}>
                        <span className="text-outline">Machined.</span>
                    </RevealLine>
                    <RevealLine delay={0.54}>
                        Legacy <span className="text-copper">Delivered.</span>
                    </RevealLine>
                </h1>

                <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
                    <motion.p
                        data-testid="hero-subtext"
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
                        className="md:col-span-5 text-base md:text-lg leading-relaxed text-[#595959] max-w-md"
                    >
                        Manufacturers of parallel keys, lead screws, shafts and slide plates —
                        trusted by India's heavy machinery builders for over four decades.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 1, ease: EASE }}
                        className="md:col-span-7 flex flex-wrap items-center gap-4 md:justify-end"
                    >
                        <button
                            data-testid="hero-explore-products-button"
                            onClick={() => lenis?.scrollTo("#products", { offset: -64, duration: 1.5 })}
                            className="group inline-flex items-center gap-3 bg-copper text-[#FAFAFA] px-7 py-4 font-semibold uppercase tracking-[0.15em] text-sm hover:bg-copper-dark transition-colors duration-300"
                        >
                            Explore Products
                            <ArrowDown size={16} className="transition-transform duration-300 group-hover:translate-y-1" />
                        </button>
                        <button
                            data-testid="hero-get-quote-button"
                            onClick={() => lenis?.scrollTo("#contact", { offset: -64, duration: 1.6 })}
                            className="group inline-flex items-center gap-3 border border-[#525252] text-[#111111] px-7 py-4 font-semibold uppercase tracking-[0.15em] text-sm hover:border-copper hover:text-copper transition-colors duration-300"
                        >
                            Get a Quote
                            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                        </button>
                    </motion.div>
                </div>

                <motion.div
                    data-testid="hero-stats"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 1.2 }}
                    className="mt-14 grid grid-cols-3 border-t border-[#111111]/15"
                >
                    {STATS.map((s) => (
                        <div key={s.label} className="pt-5 pr-4">
                            <div className="font-heading text-3xl md:text-5xl font-medium text-[#111111]">
                                {s.value}
                            </div>
                            <div className="text-[11px] md:text-xs uppercase tracking-[0.2em] text-[#737373] mt-1">
                                {s.label}
                            </div>
                        </div>
                    ))}
                </motion.div>
            </motion.div>
        </section>
    );
};

export default Hero;
